(function () {
  const SELECTOR = '[data-favorite-root]';
  const MODAL_BTN_SELECTOR = '.c_favorite-button--modal-cta';

  // productId 単位で状態を共有する
  const sharedStates = new Map();

  /* ページ単位のキャッシュ。商品ごとに単発で問い合わせると、商品一覧では商品数ぶんの
     往復が発生する（TOP の商品163件で326本）。取得はページで1回に集約する。 */
  const countCache = new Map();        // productId -> Promise<number>
  const isFavoritedCache = new Map();  // productId -> Promise<boolean>（一覧APIが使えない時のみ）
  let favoritedSetPromise = null;      // Promise<Set<handle>|null>

  /* お気に入り一覧APIの取得単位。1ページあたりの件数と、念のための最大ページ数。
     上限は無限ループ防止のガードで、通常は total に達して先に抜ける。 */
  const LIST_PAGE_SIZE = 100;
  const LIST_PAGE_LIMIT = 20;

  const getAnonymousId = () => {
    if (window.ParteTracking?.bid) return window.ParteTracking.bid;
    const KEY = 'leeep_anonymous_user_id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(KEY, id);
    }
    return id;
  };

  const getUserParams = (enduserId) => {
    const resolvedId = enduserId || window.__customerId || '';
    if (resolvedId) return { enduser_id: resolvedId };
    return { anonymous_user_id: getAnonymousId() };
  };

  const waitForLeeepCite = (callback, count = 0) => {
    if (typeof LeeepCite !== 'undefined' && LeeepCite.site_id && LeeepCite.domain) {
      callback(String(LeeepCite.site_id));
      return;
    }
    if (count > 50) {
      console.warn('[LEEEP favorite] LeeepCite.site_id または domain が取得できませんでした');
      return;
    }
    setTimeout(() => waitForLeeepCite(callback, count + 1), 100);
  };

  const fetchFavoriteCount = async (productId, websiteId) => {
    const params = new URLSearchParams({ website_id: websiteId, product_id: productId });
    const response = await fetch(
      `${LeeepCite.domain}/v3/products/favorites/counts?${params.toString()}`,
      { method: 'GET', headers: { Accept: 'application/json' } }
    );
    if (!response.ok) throw new Error(`status: ${response.status}`);
    const data = await response.json();
    const counts = Array.isArray(data.favorite_counts) ? data.favorite_counts : [];
    const matched = counts.find((item) => String(item.product_id) === String(productId));
    return matched ? Number(matched.count || 0) : 0;
  };

  const fetchIsFavorited = async (productId, websiteId, userParams) => {
    const params = new URLSearchParams({ website_id: websiteId, product_id: productId, ...userParams });
    const response = await fetch(
      `${LeeepCite.domain}/v3/products/favorites?${params.toString()}`,
      { method: 'GET', headers: { Accept: 'application/json' } }
    );
    if (!response.ok) throw new Error(`status: ${response.status}`);
    const data = await response.json();
    const ids = Array.isArray(data.favorited_product_ids) ? data.favorited_product_ids : [];
    return ids.map(String).includes(String(productId));
  };

  /* 一覧APIは product_url（商品URL）と product_id を返す。カード側の data-product-id は
     handle だが、登録POST時は handle を product_id として送っているため、どちらの
     フィールドでも一致しうる。sections/c_mypage_favorite-products.liquid の extractHandle、
     assets/c_karte-favorite.js の extractHandle(item.product_url) || item.product_id と
     同じ考え方。 */
  const extractHandle = (productUrl) => {
    if (!productUrl) return '';
    const path = String(productUrl).split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  };

  /* 顧客のお気に入り商品を全件取得して handle（または product_id）の集合にする。
     商品ごとに /v3/products/favorites を叩く代わりに、ここ1回でページ内の全カードの
     状態が決まる。エンドポイントは c_mypage_favorite-products.liquid で稼働中のもの。
     LIST_PAGE_LIMIT に達しても total まで取り切れなかった場合は、Set が不完全なまま
     確定させず Error を投げる。呼び出し側の catch でフォールバック（商品単位の取得）に回る。 */
  const fetchFavoritedHandles = async (websiteId, userParams) => {
    const handles = new Set();
    let offset = 0;
    let completed = false;
    for (let page = 0; page < LIST_PAGE_LIMIT; page++) {
      const params = new URLSearchParams({
        website_id: websiteId,
        count: String(LIST_PAGE_SIZE),
        offset: String(offset),
        ...userParams
      });
      const response = await fetch(
        `${LeeepCite.domain}/v3/products/favorites/list?${params.toString()}`,
        { method: 'GET', headers: { Accept: 'application/json' } }
      );
      if (!response.ok) throw new Error(`status: ${response.status}`);
      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      products.forEach((item) => {
        const handle = extractHandle(item && item.product_url);
        if (handle) handles.add(handle);
        if (item && item.product_id) handles.add(String(item.product_id));
      });
      offset += products.length;
      if (products.length === 0 || offset >= Number(data.total || 0)) {
        completed = true;
        break;
      }
    }
    if (!completed) throw new Error('favorites list did not complete within LIST_PAGE_LIMIT');
    return handles;
  };

  /* 一覧APIの結果はページで1回だけ取得して共有する。
     取得できない場合（未対応のユーザー種別・APIエラー）は null を返し、
     呼び出し側は「画面内に入ったカードだけ商品単位で問い合わせる」方に切り替える。 */
  const getFavoritedSet = (websiteId, userParams) => {
    if (!favoritedSetPromise) {
      favoritedSetPromise = fetchFavoritedHandles(websiteId, userParams).catch((error) => {
        console.warn('[LEEEP favorite] お気に入り一覧を取得できないため商品単位の取得に切り替えます', error);
        return null;
      });
    }
    return favoritedSetPromise;
  };

  const getFavoriteCount = (productId, websiteId) => {
    if (!countCache.has(productId)) {
      countCache.set(productId, fetchFavoriteCount(productId, websiteId));
    }
    return countCache.get(productId);
  };

  const getIsFavorited = (productId, websiteId, userParams) => {
    if (!isFavoritedCache.has(productId)) {
      isFavoritedCache.set(productId, fetchIsFavorited(productId, websiteId, userParams));
    }
    return isFavoritedCache.get(productId);
  };

  /* トグル後もキャッシュを実態に合わせる。タブ切替で再バインドされた時に、
     取得し直さずに正しい状態を復元できるようにする。 */
  const updateFavoritedCache = (productId, isFavorited) => {
    if (favoritedSetPromise) {
      favoritedSetPromise.then((favoritedSet) => {
        if (!favoritedSet) return;
        if (isFavorited) favoritedSet.add(String(productId));
        else favoritedSet.delete(String(productId));
      });
    }
    if (isFavoritedCache.has(productId)) {
      isFavoritedCache.set(productId, Promise.resolve(isFavorited));
    }
    if (countCache.has(productId)) {
      const next = countCache.get(productId)
        .then((count) => Math.max(0, Number(count) + (isFavorited ? 1 : -1)));
      next.catch(() => { /* 元の取得失敗は取得時に警告済み */ });
      countCache.set(productId, next);
    }
  };

  /* 画面内（手前200px）に入ったら1度だけ通知する。商品カードは SP用/PC用の2つが
     出力され、表示されない側は display:none で交差しないため、同じ商品の複数ルートを
     まとめて監視できるようにしてある。IntersectionObserver が無い環境では即時に通知する。 */
  const createVisibility = () => {
    const elements = [];
    let callbacks = [];
    let observer = null;
    let isVisible = false;

    const notify = () => {
      if (isVisible) return;
      isVisible = true;
      if (observer) observer.disconnect();
      const pending = callbacks;
      callbacks = [];
      pending.forEach((callback) => callback());
    };

    /* 監視は必要になった時だけ始める（一覧APIで解決できた場合は監視そのものが不要）。 */
    const start = () => {
      if (isVisible || observer) return;
      if (typeof IntersectionObserver === 'undefined') {
        notify();
        return;
      }
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) notify();
      }, { rootMargin: '200px' });
      elements.forEach((el) => observer.observe(el));
    };

    return {
      observe(el) {
        if (isVisible || !el) return;
        elements.push(el);
        if (observer) observer.observe(el);
      },
      onVisible(callback) {
        if (isVisible) {
          callback();
          return;
        }
        callbacks.push(callback);
        start();
      }
    };
  };

  const addFavorite = async (productId, websiteId, userParams) => {
    const response = await fetch(
      `${LeeepCite.domain}/v3/products/${productId}/favorite`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ website_id: websiteId, ...userParams })
      }
    );
    if (!response.ok) throw new Error(`status: ${response.status}`);
  };

  const removeFavorite = async (productId, websiteId, userParams) => {
    const response = await fetch(
      `${LeeepCite.domain}/v3/products/${productId}/favorite`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ website_id: websiteId, ...userParams })
      }
    );
    if (!response.ok) throw new Error(`status: ${response.status}`);
  };

  const bindOne = (root) => {
    if (root.getAttribute('data-favorite-bound') === 'true') return;
    root.setAttribute('data-favorite-bound', 'true');

    const productId = root.dataset.productId;
    const enduserId = root.dataset.enduserId || '';
    const countEl = root.querySelector('[data-favorite-count]');
    const btn = root.querySelector('[data-favorite-button]');

    // APIの初期化待ちの間も、カード側のリンク処理へクリックを渡さない。
    if (btn) btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    // 同じ productId が既にバインド済みの場合は state を共有
    if (sharedStates.has(productId)) {
      const shared = sharedStates.get(productId);
      // 既存ルートがまだ DOM に存在するか確認（セクションリロード対策）
      const hasLiveRoot = shared.roots.some(({ btn: b }) => b && document.contains(b));
      if (hasLiveRoot) {
        shared.roots.push({ btn, countEl });
        // 表示判定は同じ商品のルートをまとめて見る（SP用/PC用のうち表示される側で交差する）
        shared.visibility.observe(root);
        // handleToggle が確定したらクリックリスナーを追加し現在の状態を反映
        shared.ready.then((handleToggle) => {
          if (btn) btn.addEventListener('click', handleToggle);
          if (btn) {
            btn.dataset.favoriteState = shared.state.isFavorited ? 'active' : 'inactive';
            btn.setAttribute('aria-pressed', String(shared.state.isFavorited));
          }
          // 件数の表示先を持つルートが後から加わった場合はここで取得を始める
          if (countEl && shared.ensureCount) shared.ensureCount();
        });
        return;
      }
      // 古いエントリを削除してプライマリとして再バインド
      sharedStates.delete(productId);
    }

    // 初回バインド: 共有状態を作成
    // hasToggled: トグル成功済みフラグ。一覧取得中にユーザーがトグルした後に初期取得が
    // 解決すると、トグル前の古い結果で state/UI を上書きしてしまうためのガード。
    const state = { isFavorited: false, isLoading: false, hasToggled: false };
    const roots = [{ btn, countEl }];
    const visibility = createVisibility();
    visibility.observe(root);
    let resolveReady;
    const ready = new Promise((resolve) => { resolveReady = resolve; });
    /* エントリは実体で持ち回す。websiteId 待ちの間にタブ切替でエントリが差し替わると、
       sharedStates から引き直した先が別の商品カード群になりうる。 */
    const sharedEntry = { state, roots, ready, visibility };
    sharedStates.set(productId, sharedEntry);

    const updateUI = () => {
      roots.forEach(({ btn: b }) => {
        if (!b) return;
        b.dataset.favoriteState = state.isFavorited ? 'active' : 'inactive';
        b.setAttribute('aria-pressed', String(state.isFavorited));
      });
      const modalBtn = document.querySelector(`${MODAL_BTN_SELECTOR}[data-product-id="${productId}"]`);
      if (modalBtn) {
        modalBtn.dataset.favoriteState = state.isFavorited ? 'active' : 'inactive';
        modalBtn.setAttribute('aria-pressed', String(state.isFavorited));
      }
    };

    waitForLeeepCite((websiteId) => {
      const userParams = getUserParams(enduserId);

      /* 件数の取得。表示先が画面内に入ってから取りに行く。 */
      const ensureCount = () => {
        visibility.onVisible(() => {
          getFavoriteCount(productId, websiteId).then((count) => {
            roots.forEach(({ countEl: el }) => {
              if (el) el.textContent = String(count);
            });
          }).catch((error) => {
            console.warn('[LEEEP favorite count error]', error);
          });
        });
      };
      sharedEntry.ensureCount = ensureCount;

      /* お気に入り状態。一覧APIが使えればページ内の全カードぶんを1リクエストで決める。
         使えない場合だけ、画面内に入ったカードに限って商品単位で問い合わせる。 */
      getFavoritedSet(websiteId, userParams).then((favoritedSet) => {
        // 取得中にユーザーが既にトグルしていたら、古い結果で上書きしない
        if (state.hasToggled) return;
        if (favoritedSet) {
          state.isFavorited = favoritedSet.has(String(productId));
          updateUI();
          return;
        }
        visibility.onVisible(() => {
          getIsFavorited(productId, websiteId, userParams).then((isFavorited) => {
            if (state.hasToggled) return;
            state.isFavorited = isFavorited;
            updateUI();
          }).catch((error) => {
            console.warn('[LEEEP favorite init error]', error);
          });
        });
      });

      /* 件数は [data-favorite-count] がある時だけ取る。商品一覧のカードには件数の
         表示先が無く（snippets/product-form.liquid の商品詳細のみに存在）、取得しても
         結果の行き先が無いまま商品数ぶんのリクエストが丸ごと無駄になる。 */
      if (countEl) ensureCount();

      const handleToggle = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (state.isLoading) return;
        state.isLoading = true;
        try {
          if (state.isFavorited) {
            await removeFavorite(productId, websiteId, userParams);
            state.hasToggled = true;
            updateFavoritedCache(productId, false);
            // KARTE 側の通知失敗でお気に入りのUI更新を止めないよう独立して握りつぶす
            try {
              document.dispatchEvent(new CustomEvent('c_karte:favorite', { detail: { handle: productId, operation: 'remove' } }));
            } catch (e) { /* noop */ }
            state.isFavorited = false;
            roots.forEach(({ countEl: el }) => {
              if (el) el.textContent = String(Math.max(0, Number(el.textContent) - 1));
            });
          } else {
            await addFavorite(productId, websiteId, userParams);
            state.hasToggled = true;
            updateFavoritedCache(productId, true);
            // KARTE 側の通知失敗でお気に入りのUI更新を止めないよう独立して握りつぶす
            try {
              document.dispatchEvent(new CustomEvent('c_karte:favorite', { detail: { handle: productId, operation: 'add' } }));
            } catch (e) { /* noop */ }
            state.isFavorited = true;
            roots.forEach(({ btn: b, countEl: el }) => {
              if (el) el.textContent = String(Number(el.textContent) + 1);
              if (b) {
                b.classList.remove('is-favorite-pop');
                void b.offsetWidth;
                b.classList.add('is-favorite-pop');
                b.addEventListener('animationend', () => b.classList.remove('is-favorite-pop'), { once: true });
              }
            });
            const modalBtn = document.querySelector(`${MODAL_BTN_SELECTOR}[data-product-id="${productId}"]`);
            if (modalBtn) {
              modalBtn.classList.remove('is-favorite-pop');
              void modalBtn.offsetWidth;
              modalBtn.classList.add('is-favorite-pop');
              modalBtn.addEventListener('animationend', () => modalBtn.classList.remove('is-favorite-pop'), { once: true });
            }
          }
          updateUI();
        } catch (error) {
          console.warn('[LEEEP favorite toggle error]', error);
        } finally {
          state.isLoading = false;
        }
      };

      if (btn) btn.addEventListener('click', handleToggle);

      const modalBtn = document.querySelector(`${MODAL_BTN_SELECTOR}[data-product-id="${productId}"]`);
      if (modalBtn && !modalBtn.dataset.favoriteBound) {
        modalBtn.dataset.favoriteBound = 'true';
        modalBtn.addEventListener('click', handleToggle);
      }

      resolveReady(handleToggle);
    });
  };

  const bindAll = () => {
    document.querySelectorAll(SELECTOR).forEach(bindOne);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
  document.addEventListener('shopify:section:load', bindAll);
  // Boostの初回描画・絞り込み・追加読み込みで後から挿入されるカードにも適用する。
  const cardObserver = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches(SELECTOR)) bindOne(node);
      node.querySelectorAll(SELECTOR).forEach(bindOne);
    }));
  });
  cardObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.bindFavoriteButtons = bindAll;
})();
